import { Component, computed, effect, HostBinding, inject, input, Input, untracked } from '@angular/core';
import {
  ShirtCategoryLookup,
  TShirtCategoryLookup,
  TShirtSizes,
  TShirtSizesKeys,
} from '@ag-common-lib/lib/models/utils/t-shirt-sizes.model';
import { LookupKeys } from '@ag-common-lib/lib/models/crm/lookup.model';
import { Messages } from '@ag-common-lib/lib/constants/validation.constants';
import { InitializedEvent } from 'devextreme/ui/form';
import { BehaviorSubject } from 'rxjs';
import { ScreenLimits } from '@ag-common-lib/lib/constants/form.constants';
import { TShirtSizesFormService } from 'ag-common-svc/lib/components/t-shirt-sizes-form/t-shirt-sizes-form.service';

@Component({
  selector: 'ag-shr-t-shirt-sizes-form',
  templateUrl: './t-shirt-sizes-form.component.html',
  styleUrl: './t-shirt-sizes-form.component.scss',
  providers: [TShirtSizesFormService],
})
export class TShirtSizesFormComponent {
  @HostBinding('class') protected className = 't-shirt-sizes-form';

  private tShirtSizesFormService = inject(TShirtSizesFormService);

  @Input() readOnly = false;
  @Input() validationGroup?: string;
  @Input() pathPrefix?: string;
  @Input() dropdownsAlwaysVisible: boolean = false;
  @Input() isReverseOrder: boolean = false;
  @Input() screenLimits: ScreenLimits;
  @Input() required: boolean = false;

  tShirtSizesSig = input.required<Partial<TShirtSizes>>({
    alias: 'formData',
  });

  tShirtBlockVisibleSig = input(true, { alias: 'tShirtBlockVisible' });
  shirtBlockVisibleSig = input(true, { alias: 'shirtBlockVisible' });
  tShirtCategoryVisibleSig = input(true, { alias: 'tShirtCategoryVisible' });
  tShirtLabelSig = input<string>('', { alias: 'tShirtLabel' });
  shirtLabelSig = input<string>('', { alias: 'shirtLabel' });
  tShirtNoteSig = input<string>('', { alias: 'tShirtNote' });
  layoutSig = input<'horizontal' | 'vertical'>('vertical', { alias: 'layout' });
  sectionNotePaddingSig = input(true, { alias: 'sectionNotePadding' });

  protected readonly colCountByScreenSig = computed(() => {
    const layout = this.layoutSig();

    const baseColCount = layout === 'horizontal' ? 4 : 1;

    return {
      xs: 1,
      sm: 1,
      md: 1,
      lg: baseColCount,
      xl: baseColCount,
    } as const;
  });

  protected readonly innerColCountByScreenSig = computed(() => {
    const layout = this.layoutSig();
    const baseColCount = layout === 'horizontal' ? 3 : 1;

    return {
      xs: 1,
      sm: 1,
      md: 1,
      lg: baseColCount,
      xl: baseColCount,
    } as const;
  });

  protected formWidth$ = new BehaviorSubject<number>(0);
  protected formDataSig = this.tShirtSizesFormService.formDataSig;

  protected readonly TShirtSizesKeys = TShirtSizesKeys;
  protected readonly ShirtCategoryLookup = ShirtCategoryLookup;
  protected readonly TShirtCategoryLookup = TShirtCategoryLookup;
  protected readonly LookupKeys = LookupKeys;
  protected readonly Messages = Messages;

  constructor() {
    effect(
      () => {
        const tShirtSizes = this.tShirtSizesSig();
        const tShirtCategoryVisible = this.tShirtCategoryVisibleSig();
        untracked(() => {
          if (tShirtSizes) {
            this.tShirtSizesFormService.getFormData(tShirtSizes, tShirtCategoryVisible, this.pathPrefix);
          }
        });
      },
      { allowSignalWrites: true },
    );
  }

  protected onInit = (e: InitializedEvent) => {
    const nativeElement = e.component.$element()?.[0];

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.target === nativeElement) {
          const width = entry.contentRect.width;

          this.formWidth$.next(width);
        }
      }
    });

    resizeObserver.observe(nativeElement);
  };
}
